/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { SafePaddingContainer } from "@/components/SafePaddingContainer";
import { Button } from "@/components/ui/button";
import { CheckBox } from "@/components/CheckBox/CheckBox";
import { faCircleRight } from "@fortawesome/free-solid-svg-icons/faCircleRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useViewTransition } from "@/hooks/useViewTransition";
import { useSwipeDetection } from "@/hooks/useSwipeDetection";
import { useIsLocationAllowed } from "@/hooks/useIsLocationAllowed";
import { useCallback } from "react";
import { PersistentData } from "@/helpers/PersistentData";


// Function to generate promo codes based on location name
const generatePromoCodes = (locationName: string | undefined): Record<string, number> => {
  const defaultLocation = "FREE";
  const location = locationName || defaultLocation;
  
  return {
    [`${location}5`]: 20,
    "FREE5": 5,
  };
};

// Component for the promo code input form
const PromoCodeInput = ({ 
  promoCode, 
  setPromoCode, 
  handleApply, 
  handleAutoApplyPromo,
  errorMessage,
}: {
  promoCode: string;
  setPromoCode: (value: string) => void;
  handleApply: () => void;
  handleClear: () => void;
  handleAutoApplyPromo: () => void;
  errorMessage: string | null;
}): JSX.Element => {
  return (
    <>
      <div className="relative mt-6">
        <input
          type="text"
          placeholder="Promo code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          className={`w-full h-12 px-4 text-xl text-slate font-bold bg-white rounded-md placeholder:text-gray-400 placeholder:font-bold focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-transparent ${errorMessage ? 'border-2 border-red-500' : ''}`}
        />
        {promoCode.length > 0 && (
            <Button
            onClick={handleApply}
            className="absolute text-white bg-slate font-bold border-none flex items-center top-2 right-2"
            size='sm'
            aria-label="Apply promo code"
          >
            <span className="mr-1 text-sm">Apply</span>
          </Button>
        )}
      </div>
      {errorMessage && (
        <p className="text-sm text-red-500 font-medium mt-1">
          {errorMessage}
        </p>
      )}
      <div className="flex items-center mt-5">
        <p className="text-sm text-white font-bold whitespace-nowrap">
          Don't have one? 
        </p>
        <Button
          className="text-sm font-bold text-blue bg-white rounded-lg ml-3"
          size='sm'
          onClick={handleAutoApplyPromo}
        >
          Tap for a free $5
        </Button>
      </div>
    </>
  );
};

// Component for the applied promo code view
const AppliedPromoCode = ({
  promoCode,
  resetPromo,
  promoValue
}: {
  promoCode: string;
  resetPromo: () => void;
  promoValue: number;
}): JSX.Element => {
    const [isChecked, setIsChecked] = useState<boolean>(true);
    const { navigateNormal } = useViewTransition(); 

  return (
    <>
      <div className="w-full h-12 px-4 mt-6 flex items-center justify-between text-xl font-bold text-white bg-green rounded-lg">
        <span>Promo {promoCode} applied!</span>
        <Button
          onClick={resetPromo}
          className="text-white bg-slate font-bold border-none flex items-center"
          size='sm'
          aria-label="Clear promo code"
        >
          <span className="mr-1 text-sm">Clear</span>
        </Button>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center mt-5">
          <div className="relative w-6 h-6 flex-shrink-0 mr-1">
            <div className='mt-[3px]'>
              <CheckBox
                  id="freeCredit"
                  checked={true}
                  onChange={() => {}}
                  backgroundColor="#37dd60"
                  color="fff"
                  disabled={true}
              />
            </div>
          </div>
          <label htmlFor="freeCredit" className="text-sm text-white cursor-pointer font-bold whitespace-nowrap mt-auto mb-0">
            $5 free credit to start (instantly available)
          </label>
        </div>
      </div>
      {
        promoValue > 5 &&
        <div className="flex justify-between items-center">
          <div className="flex mt-2">
            <div className="relative w-6 h-6 flex-shrink-0 mr-1">
              <div className='mt-[3px]'>
                <CheckBox
                    id="freeCredit"
                    checked={isChecked}
                    onChange={() => {setIsChecked(!isChecked)}}
                    backgroundColor="#37dd60"
                    color="fff"
                />
              </div>
            </div>
            <label htmlFor="freeCredit" className="text-sm text-white cursor-pointer font-bold mt-auto mb-0">
              $15 free trial entry into weekly jackpot drawing (recurs weekly starting in 7 days, 1-tap to cancel)
            </label>
          </div>
        </div>
      }
      <Button
        className="w-full h-12 mt-6 text-xl font-medium text-white bg-blue rounded-lg flex items-center justify-center font-bold"
        onClick={() => {
          navigateNormal("/onboarding/page2",
            {
              replace: false,
              viewTransition: true,
            }
          );
        }}
      >
        <span>
          Awesome!           
        <FontAwesomeIcon
            icon={faCircleRight}
            style={{
              display: "inline-block ml-0",
              marginInlineStart: "0.3em",
            }}
            className="mb-[1px]"
          />
        </span>  
      </Button>
    </>
  );
};

export function GotAPromoPage(): JSX.Element {
  const [isPromoApplied, setIsPromoApplied] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPromoValue, setCurrentPromoValue] = useState<number>(5);
  const [isLocationAllowed] = useIsLocationAllowed();
  const [promoCodes, setPromoCodes] = useState<Record<string, number>>({});
  const [defaultPromoCode, setDefaultPromoCode] = useState<string>("");

  const { navigateBack } = useViewTransition();
  
  // Initialize promo codes and default promo code based on location
  useEffect(() => {
    const locationName = isLocationAllowed?.locationName || "FREE";
    const codes = generatePromoCodes(locationName);
    setPromoCodes(codes);
    
    // 50-50 chance to get either the 5 or 20 value promo code as default Edit: no longer 50-50 chance as per A/B test
    const randomValue = 20;
    const defaultCode = `${locationName}5`;
    PersistentData.set('freetrial', randomValue === 20);
    setDefaultPromoCode(defaultCode);
  }, [isLocationAllowed]);

  // Validation function using the promoCodes object
  const isValidPromo = useCallback((code: string): boolean => {
    return code in promoCodes;
  }, [promoCodes]);
  
  const handleBack = () => {
    navigateBack();
  };

  useSwipeDetection({
    minSwipeDistance: 50,
    onSwipeRight: () => {
      handleBack();
    }
  });
  
  // Auto-apply promo when valid code is entered
  useEffect(() => {
    if (promoCode && isValidPromo(promoCode)) {
      setCurrentPromoValue(promoCodes[promoCode]);
      setIsPromoApplied(true);
      setErrorMessage(null);
    }
  }, [promoCode, isValidPromo, promoCodes]);

  const handleApply = (): void => {
    if (isValidPromo(promoCode)) {
      setCurrentPromoValue(promoCodes[promoCode]);
      setIsPromoApplied(true);
      setErrorMessage(null);
    } else if (promoCode.trim() === "") {
      setErrorMessage("Please enter a promo code");
    } else {
      setErrorMessage(`Invalid promo code. Try ${defaultPromoCode}`);
    }
  };
  
  // Auto-fill and apply the default promo code
  const handleAutoApplyPromo = (): void => {
    setPromoCode(defaultPromoCode);
    setCurrentPromoValue(promoCodes[defaultPromoCode] || 5);
    setIsPromoApplied(true);
    setErrorMessage(null);
  };

  const handleClear = (): void => {
    setPromoCode("");
    setErrorMessage(null);
  };
  
  const resetPromo = (): void => {
    setPromoCode("");
    setIsPromoApplied(false);
    setErrorMessage(null);
    setCurrentPromoValue(5); // Reset to default value
  };

  return (
    <div className="flex flex-col h-screen overflow-y-scroll pb-40 bg-blue-dark">
      <SafePaddingContainer orientation="header" className="bg-blue-dark h-full">
        <h1 className="text-3xl font-bold text-white">
          First up, got a promo? 
        </h1>

        {!isPromoApplied ? (
          <PromoCodeInput 
            promoCode={promoCode}
            setPromoCode={(value) => setPromoCode(value.toUpperCase())}
            handleApply={handleApply}
            handleClear={handleClear}
            handleAutoApplyPromo={handleAutoApplyPromo}
            errorMessage={errorMessage}
          />
        ) : (
          <AppliedPromoCode 
            promoCode={promoCode}
            resetPromo={resetPromo}
            promoValue={currentPromoValue}
          />
        )}
      </SafePaddingContainer>
    </div>
  );
}